import { useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { CodeTabs } from '@/components/CodeTabs';
import { PayloadViewer } from '@/components/PayloadViewer';
import { ResponseViewer } from '@/components/ResponseViewer';
import { SequenceDiagram, type SequenceStep } from '@/components/SequenceDiagram';
import { executeRequest } from '@/api/client';
import type { ApiResponseSnapshot, RequestSpec } from '@/types';

interface PanelProps {
  title: string;
  description: string;
  spec: RequestSpec;
  mode: 'preview' | 'live';
  children: React.ReactNode;
  runLabel?: string;
  /** Optional eyebrow shown above the title (e.g. "Endpoint"). */
  eyebrow?: string;
  /** Optional sequence-diagram strip rendered above the title — gives the
   *  developer context on where this single endpoint fits in the wider
   *  pipeline. */
  sequence?: { steps: SequenceStep[]; activeId: string };
}

/**
 * Shared layout for skeleton playgrounds: form on the left, payload + code +
 * (live) response on the right, with a single "Run" button that fires the
 * built `spec` through the API client.
 */
export function PlaygroundPanel({ title, description, spec, mode, children, runLabel, eyebrow, sequence }: PanelProps) {
  const { env, getValidAccessToken } = useAuth();
  const [response, setResponse] = useState<ApiResponseSnapshot | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setError(null);
    setRunning(true);
    try {
      const token = getValidAccessToken();
      if (!token) throw new Error('Your session expired — sign in again.');
      const r = await executeRequest(env, spec, token);
      setResponse(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="lp-space-y-5">
      {sequence && (
        <SequenceDiagram steps={sequence.steps} activeId={sequence.activeId} eyebrow="Pipeline" />
      )}
      <div className="lp-grid lp-grid-cols-1 xl:lp-grid-cols-[minmax(0,1.35fr)_minmax(360px,440px)] lp-gap-7">
        <div className="lp-space-y-5">
          <div>
            {eyebrow && (
              <div
                className="lp-text-[10.5px] lp-font-bold lp-uppercase lp-mb-1"
                style={{ letterSpacing: '0.14em', color: '#6256ff' }}
              >
                {eyebrow}
              </div>
            )}
            <h1 className="lp-text-2xl lp-font-bold lp-tracking-tight" style={{ color: '#0f172a' }}>
              {title}
            </h1>
            <p className="lp-text-sm lp-mt-1.5" style={{ color: '#475569' }}>
              {description}
            </p>
          </div>
          {children}

          {mode === 'live' && (
            <button
              onClick={run}
              disabled={running}
              className="lp-btn-primary lp-w-full lp-py-3 lp-text-sm lp-font-bold"
              style={{ borderRadius: 12 }}
            >
              {running ? 'Running…' : runLabel || `Send ${spec.method} ${spec.path}`}
            </button>
          )}
        </div>

        <div className="lp-space-y-5 xl:lp-sticky xl:lp-top-6 lp-self-start">
          <PayloadViewer spec={spec} />
          <CodeTabs env={env} spec={spec} hasAuth />
          {mode === 'live' && <ResponseViewer response={response} loading={running} error={error} />}
        </div>
      </div>
    </div>
  );
}

export function FormCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="lp-p-5 lp-space-y-3 lp-shadow-card"
      style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 22 }}
    >
      {children}
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="lp-block">
      <div className="lp-text-[11px] lp-font-semibold lp-text-ink-500 lp-uppercase lp-tracking-[0.08em]">{label}</div>
      {hint && <div className="lp-text-[11px] lp-text-ink-400 lp-mt-0.5">{hint}</div>}
      <div className="lp-mt-1.5">{children}</div>
    </label>
  );
}

const inputBase =
  'lp-w-full lp-bg-white lp-border lp-border-ink-200 lp-rounded-xl lp-px-3 lp-py-2 lp-text-sm lp-text-ink-900 placeholder:lp-text-ink-400 lp-focus-ring lp-transition-shadow';

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${inputBase} ${props.className || ''}`} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${inputBase} lp-resize-none ${props.className || ''}`} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${inputBase} ${props.className || ''}`} />;
}

/**
 * Banner shown when a playground requires an `organization_id` but the user
 * hasn't set one yet in the top-bar Org input. Renders nothing if the id is
 * present.
 */
export function OrgIdRequired({ organizationId }: { organizationId: string }) {
  if (organizationId) return null;
  return (
    <div
      className="lp-px-4 lp-py-3 lp-text-sm lp-flex lp-items-start lp-gap-3"
      style={{
        borderRadius: 12,
        border: '1px solid #cbc4ff',
        background: '#f8f7ff',
        color: '#334155',
      }}
    >
      <span
        className="lp-mt-0.5 lp-w-5 lp-h-5 lp-rounded-full lp-text-white lp-text-[11px] lp-font-bold lp-flex lp-items-center lp-justify-center lp-shrink-0"
        style={{ background: 'linear-gradient(135deg, #6256ff, #7c3aed)' }}
      >
        i
      </span>
      <span>
        <span className="lp-font-semibold" style={{ color: '#0f172a' }}>Pick an organization.</span>{' '}
        Use the <strong>Org</strong> dropdown in the top bar — once an org is selected the playground will inject its
        id into the URL automatically.
      </span>
    </div>
  );
}

interface TagsInputProps {
  /** Array of `{ tag_key, tag_value }` (Libertify's EntityTag shape). */
  value: { tag_key: string; tag_value: string }[];
  onChange: (next: { tag_key: string; tag_value: string }[]) => void;
}

/**
 * Tag editor matching Libertify's EntityTag schema. The backend requires
 * `tag_key === tag_value` for every tag, so the UI exposes a single label
 * input per tag and silently mirrors the value into both fields on emit.
 * Empty rows are stripped.
 */
export function TagsInput({ value, onChange }: TagsInputProps) {
  // Derive a single editable label per row from whichever field is filled
  // (some tags coming from the backend may already have asymmetric values
  // — show the key by default, the user can edit it freely).
  const labels = value.length === 0
    ? ['']
    : value.map((t) => t.tag_key || t.tag_value || '');

  const emit = (next: string[]) => {
    onChange(
      next
        .map((label) => label.trim())
        .filter(Boolean)
        .map((label) => ({ tag_key: label, tag_value: label }))
    );
  };

  const update = (i: number, label: string) => {
    const next = [...labels];
    next[i] = label;
    emit(next);
  };
  const add = () => onChange([
    ...value,
    // intentionally keep both fields blank — they're added to state but
    // dropped on emit (filterBoolean) so the JSON stays clean.
    { tag_key: '', tag_value: '' },
  ]);
  const remove = (i: number) => {
    const next = labels.filter((_, idx) => idx !== i);
    emit(next);
  };

  const tagInputClass =
    'lp-flex-1 lp-bg-white lp-border lp-border-ink-200 lp-rounded-xl lp-px-3 lp-py-2 lp-text-sm lp-font-mono lp-text-ink-900 placeholder:lp-text-ink-400 lp-focus-ring lp-transition-shadow';

  return (
    <div className="lp-space-y-2">
      {labels.map((label, i) => (
        <div key={i} className="lp-flex lp-gap-2 lp-items-center">
          <input
            type="text"
            value={label}
            onChange={(e) => update(i, e.target.value)}
            placeholder="tag label (e.g. playground)"
            className={tagInputClass}
          />
          {labels.length > 1 && (
            <button
              type="button"
              onClick={() => remove(i)}
              className="lp-px-2 lp-text-ink-400 hover:lp-text-rose-600"
              aria-label="Remove tag"
            >
              ×
            </button>
          )}
        </div>
      ))}
      <div className="lp-flex lp-items-center lp-justify-between lp-gap-3">
        <button
          type="button"
          onClick={add}
          className="lp-text-xs lp-font-semibold lp-text-brand-600 hover:lp-text-brand-700"
        >
          + Add tag
        </button>
        <span className="lp-text-[10.5px] lp-text-ink-400">
          Each label is sent as <code className="lp-bg-ink-100 lp-px-1 lp-rounded">{'{'}tag_key, tag_value{'}'}</code> with both fields equal.
        </span>
      </div>
    </div>
  );
}
